/**
 * Phase 3b-2 — protest safe-lane workspace.
 * Reached at /protest/:projectId within the dashboard layout.
 *
 * Open a protest watch (award → debrief → the server-computed 10-day awareness
 * window), update its status, and attach PUBLIC GAO decisions for pattern
 * study. No privileged content here — that's the Phase 3c legal-restricted lane.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorState } from '../../components/layout';
import { PROTEST_DECISION_PACKET_TYPE, useGenerateFirstDraft } from '../../hooks/useArtifactStudio';
import { PROTEST_LIKELIHOODS, PROTEST_STATUSES, useAddPublicProtestDecision, useOpenProtestWatch, useProtestDecisions, useProtestWatches, useUpdateProtestWatch } from './protestHooks';
function fmtDate(ts) {
    if (!ts) return '—';
    return ts.toDate().toISOString().slice(0, 10);
}
function daysUntil(ts) {
    if (!ts) return null;
    return Math.ceil((ts.toDate().getTime() - Date.now()) / (24 * 3600 * 1000));
}
export default function ProtestWorkspacePage() {
    const { projectId } = useParams();
    if (!projectId) {
        return /*#__PURE__*/ _jsx("div", {
            className: "p-6 text-red-600",
            children: "Missing projectId in the URL."
        });
    }
    return /*#__PURE__*/ _jsx(ProtestWorkspace, {
        projectId: projectId
    });
}
function ProtestWorkspace({ projectId }) {
    const watchesQuery = useProtestWatches(projectId);
    const open = useOpenProtestWatch();
    const [debrief, setDebrief] = useState('');
    const [likelihood, setLikelihood] = useState(PROTEST_LIKELIHOODS[0]);
    const watches = watchesQuery.data ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6 p-6",
        "data-testid": Testids.protest.panelRoot,
        children: [
            /*#__PURE__*/ _jsxs("header", {
                children: [
                    /*#__PURE__*/ _jsx("h1", {
                        className: "text-xl font-semibold",
                        children: "Protest Safe Lane"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "Track the post-award protest window (debrief → 10-day GAO awareness window), the triage read, and similar PUBLIC GAO decisions."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "space-y-3 rounded border border-gray-200 p-4",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "font-medium",
                        children: "Open a watch"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-end gap-3",
                        children: [
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex flex-col text-sm",
                                children: [
                                    "Debrief date",
                                    /*#__PURE__*/ _jsx("input", {
                                        type: "date",
                                        "data-testid": Testids.protest.openDebriefInput,
                                        className: "rounded border px-2 py-1",
                                        value: debrief,
                                        onChange: (e)=>setDebrief(e.target.value)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex flex-col text-sm",
                                children: [
                                    "Likelihood",
                                    /*#__PURE__*/ _jsx("select", {
                                        "data-testid": Testids.protest.openLikelihoodSelect,
                                        className: "rounded border px-2 py-1",
                                        value: likelihood,
                                        onChange: (e)=>setLikelihood(e.target.value),
                                        children: PROTEST_LIKELIHOODS.map((l)=>/*#__PURE__*/ _jsx("option", {
                                                value: l,
                                                children: l
                                            }, l))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                "data-testid": Testids.protest.openSubmit,
                                className: "rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50",
                                disabled: open.isPending,
                                onClick: ()=>{
                                    open.mutate({
                                        projectId,
                                        debriefDate: debrief ? new Date(`${debrief}T00:00:00Z`) : undefined,
                                        protestLikelihood: likelihood
                                    }, {
                                        onSuccess: ()=>setDebrief('')
                                    });
                                },
                                children: "Open watch"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsxs("h2", {
                        className: "font-medium",
                        children: [
                            "Watches (",
                            watches.length,
                            ")"
                        ]
                    }),
                    watchesQuery.isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "Loading…"
                    }),
                    watchesQuery.isError && /*#__PURE__*/ _jsx(ErrorState, {
                        title: "Couldn't load protest watches",
                        message: "We couldn't load the protest watches for this project. This is usually temporary — try again.",
                        onRetry: ()=>void watchesQuery.refetch()
                    }),
                    !watchesQuery.isLoading && !watchesQuery.isError && watches.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "No protest watches on this project yet."
                    }),
                    watches.map((w)=>/*#__PURE__*/ _jsx(ProtestWatchRow, {
                            watch: w,
                            projectId: projectId
                        }, w.id))
                ]
            })
        ]
    });
}
function ProtestWatchRow({ watch, projectId }) {
    const update = useUpdateProtestWatch();
    const addDecision = useAddPublicProtestDecision();
    const decisionsQuery = useProtestDecisions(watch.id);
    const navigate = useNavigate();
    const generatePacket = useGenerateFirstDraft(projectId);
    const [status, setStatus] = useState(watch.status || PROTEST_STATUSES[0]);
    const [docket, setDocket] = useState('');
    const [outcome, setOutcome] = useState('');
    const remaining = daysUntil(watch.awarenessWindowEnd);
    const decisions = decisionsQuery.data ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.protest.watchRow,
        className: "space-y-3 rounded border border-gray-200 p-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        "data-testid": Testids.protest.watchStatus,
                        className: "rounded bg-gray-100 px-2 py-0.5 text-xs font-medium",
                        children: watch.status
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-xs text-gray-500",
                        children: [
                            "likelihood: ",
                            watch.protestLikelihood
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.protest.watchWindow,
                className: "rounded bg-gray-50 p-3 text-sm",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-gray-600",
                        children: "Debrief"
                    }),
                    " ",
                    fmtDate(watch.debriefDate),
                    /*#__PURE__*/ _jsx("span", {
                        className: "mx-2 text-gray-400",
                        children: "→"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-gray-600",
                        children: "Awareness window ends"
                    }),
                    ' ',
                    fmtDate(watch.awarenessWindowEnd),
                    remaining !== null && /*#__PURE__*/ _jsx("span", {
                        className: `ml-2 rounded px-2 py-0.5 text-xs ${remaining <= 3 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`,
                        children: remaining >= 0 ? `${remaining}d left` : 'window closed'
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-end gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("label", {
                        className: "flex flex-col text-xs",
                        children: [
                            "Status",
                            /*#__PURE__*/ _jsx("select", {
                                "data-testid": Testids.protest.updateStatusSelect,
                                className: "rounded border px-2 py-1",
                                value: status,
                                onChange: (e)=>setStatus(e.target.value),
                                children: PROTEST_STATUSES.map((s)=>/*#__PURE__*/ _jsx("option", {
                                        value: s,
                                        children: s
                                    }, s))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        "data-testid": Testids.protest.updateSubmit,
                        className: "rounded border px-3 py-1 disabled:opacity-50",
                        disabled: update.isPending,
                        onClick: ()=>update.mutate({
                                protestWatchId: watch.id,
                                status
                            }),
                        children: "Update"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        "data-testid": Testids.protest.generatePacketButton,
                        className: "ml-auto rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50",
                        disabled: generatePacket.isPending,
                        title: "Generate a decision packet (Artifact Studio · requires attorney review)",
                        onClick: ()=>generatePacket.mutate({
                                artifactType: PROTEST_DECISION_PACKET_TYPE
                            }, {
                                onSuccess: ()=>navigate(`/artifact-studio/${projectId}`)
                            }),
                        children: generatePacket.isPending ? 'Generating…' : 'Generate decision packet'
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rounded bg-gray-50 p-3",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-xs font-medium text-gray-600",
                        children: "Similar PUBLIC decisions"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-1 space-y-1",
                        children: decisions.map((d)=>/*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.protest.decisionRow,
                                className: "text-xs",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-mono",
                                        children: d.docketNumber
                                    }),
                                    d.outcome ? ` — ${d.outcome}` : ''
                                ]
                            }, d.id))
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-2 flex flex-wrap items-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex flex-col text-xs",
                                children: [
                                    "Docket #",
                                    /*#__PURE__*/ _jsx("input", {
                                        "data-testid": Testids.protest.addDecisionDocketInput,
                                        className: "rounded border px-2 py-1",
                                        value: docket,
                                        onChange: (e)=>setDocket(e.target.value),
                                        placeholder: "B-420000.1"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex flex-col text-xs",
                                children: [
                                    "Outcome",
                                    /*#__PURE__*/ _jsx("input", {
                                        "data-testid": Testids.protest.addDecisionOutcomeInput,
                                        className: "rounded border px-2 py-1",
                                        value: outcome,
                                        onChange: (e)=>setOutcome(e.target.value),
                                        placeholder: "corrective_action"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                "data-testid": Testids.protest.addDecisionSubmit,
                                className: "rounded border px-3 py-1 disabled:opacity-50",
                                disabled: !docket.trim() || addDecision.isPending,
                                onClick: ()=>addDecision.mutate({
                                        protestWatchId: watch.id,
                                        docketNumber: docket.trim(),
                                        outcome: outcome.trim()
                                    }, {
                                        onSuccess: ()=>{
                                            setDocket('');
                                            setOutcome('');
                                        }
                                    }),
                                children: "Add decision"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
